import * as React from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { DayPicker, NavProps } from 'react-day-picker';
import { cn } from '@/utils/utils';
import { format } from 'date-fns';
import { buttonVariants } from '../common/Button';

export type ReactDayPickerProps = React.ComponentProps<typeof DayPicker>;

function ReactDayPicker({
    className,
    classNames,
    showOutsideDays = true,
    ...props
}: ReactDayPickerProps) {
    const navigationButtonStyles = cn(
        buttonVariants({ variant: 'outlineForeground' }),
        'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
    );

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn('p-3 rounded-md border', className)}
            classNames={{
                nav: 'flex items-center justify-between',
                chevron: 'w-4 h-4',
                button_next: navigationButtonStyles,
                button_previous: navigationButtonStyles,
                caption_label: 'text-sm font-medium hidden',
                day: cn(
                    buttonVariants({ variant: 'ghost' }),
                    'size-9 xsm:h-11 xsm:w-11 font-normal text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 hover:cursor-pointer'
                ),
                day_button: 'h-11 w-11',
                selected: '!bg-primaryWeb text-white hover:bg-bg-primaryWeb hover:text-white focus:bg-bg-primaryWeb focus:text-white',
                disabled: 'text-muted-foreground opacity-50',
                focused: '',
                hidden: 'invisible',
                outside: 'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
                today: 'bg-accent text-accent-foreground',
                dropdown: 'border-none outline-none hover:cursor-pointer',
                dropdown_root: '',
                dropdowns: 'flex flex-row-reverse gap-2 w-[100px] items-center justify-center',
                footer: '',
                month: 'w-full border-collapse',
                month_caption: 'absolute top-1 left-1/2 -translate-x-1/2 flex items-center',
                month_grid: 'w-full',
                months: 'relative flex flex-col',
                months_dropdown: '',
                week: 'flex w-full xsm:mt-2 point-375:mt-1 justify-between gap-x-1',
                week_number: '',
                week_number_header: '',
                weekday: 'text-calendar-1 rounded-md w-9 font-medium',
                weekdays: 'flex h-10 items-center border-y border-border-3 justify-between mt-2.5',
                weeks: '',
                years_dropdown: '',
                ...classNames
            }}
            captionLayout='dropdown'
            components={{
                Chevron: ({ className, orientation = 'down' }) => {
                    const iconChevron = {
                        'down': ChevronDown,
                        'up': ChevronUp,
                        'left': ChevronLeft,
                        'right': ChevronRight
                    };

                    const Comp = iconChevron[orientation];

                    return (
                        <Comp className={className} />
                    );
                },
                Nav: ({ children, className, ...props }: NavProps) => {
                    const [nextButton, previousButton] = React.Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;

                    return (
                        <nav className={cn('flex items-center justify-between', className)} {...props}>
                            {nextButton}
                            {previousButton}
                        </nav>
                    );
                }
            }}
            formatters={{
                formatMonthDropdown: (month, locale) => {
                    // const formatMonth = locale?.code === LANGUAGES_MAPPER.vietnamese.code ? 'MM' : 'MMM';
                    const formatMonth = 'MMM';
                    return format(new Date().setMonth(month), formatMonth, {
                        locale
                    });
                }
            }}
            endMonth={new Date((new Date()).getFullYear() + 30, 11, 31)}
            {...props}
        />
    );
}
ReactDayPicker.displayName = 'ReactDayPicker';

export { ReactDayPicker };
